/**
 * Simple responsive image lightbox.
 * This module requires Susi.js, so make sure to include it before initializing.
 *
 * Used in conjunction with styles/objects/_lightbox.scss
 *
 * Markup example:
 * ```
 * <!-- image without meta data -->
 * <a class="en-lightbox-trigger" href="image-large-1.jpg"><img src="thumbnail-1.jpg" alt="thumbnail" /></a>
 *
 * <!-- image with meta data -->
 * <a class="en-lightbox-trigger" href="image-large-2.jpg" data-en-lightbox-caption="My image title" data-en-lightbox-description="My image description"><img src="thumbnail-2.jpg" alt="thumbnail" /></a>
 * </ul>
 * ```
 *
 * Implementation example:
 * ```
 * EN.Lightbox.init();
 * ```
 *
 * @module Lightbox
 * @author EN GARDE (Martin Holler)
 * @version 2.2
 *
 * Changelog:
 * 	2.2 	fixed bug on close when $lightbox does not exist
 * 	2.1 	fixed bug in loadImage function (used default setting instead of supplied options)
 * 	2.0		rewrite
 * 	1.0		initial version
 */

var EN = EN || {};

EN.Lightbox = (function ($) {
	'use strict';

	var defaults = {
		selector: '.en-lightbox-trigger[href]',
		scope: '.en-lightbox-group',
		container: document.body,
		classNames: {
			root: 'en-lightbox',
			loading: 'en-is-loading',
			inactive: 'en-is-inactive',
			inner: 'en-lightbox__inner',
			close: 'en-lightbox__close',
			imageWrapper: 'en-lightbox__image-wrapper',
			image: 'en-lightbox__image',
			text: 'en-lightbox__text',
			caption: 'en-lightbox__caption',
			description: 'en-lightbox__description',
			body: 'en-lightbox-open'
		},
		dataAttributes: {
			url: 'data-en-lightbox-url',
			caption: 'data-en-lightbox-caption',
			description: 'data-en-lightbox-description'
		}
	};
	var imagesLoading = 0;
	var options, $lightbox, $container;


	/**
	 * Loads the nth element's image and adds a loading class to the lightbox until the image is loaded.
	 * @param {number} index - Index of the element to load the image of.
	 * @return {jQuery}
	 */
	var loadImage = function (index) {

		// bail if there are no elements
		if (!$container.length) {
			return;
		}

		var $target = $container.children().eq(index);
		var url = $target.attr(options.dataAttributes.url);
		var $image;

		if (url) {
			$lightbox.addClass(options.classNames.loading);

			imagesLoading++;

			$image = $('<img class="' + options.classNames.image + '" src="' + url + '" alt="" />');
			$image.on('load', function () {
				imagesLoading--;

				if (!imagesLoading) {
					$lightbox.removeClass(options.classNames.loading);
				}
			});

			$target.find('.' + options.classNames.imageWrapper).prepend($image);

			$target.removeAttr(options.dataAttributes.url);
		}

		return $target;
	};

	/**
	 * Opens the lightbox element. If it doesn't exist yet, it creates the lightbox element.
	 * @memberof module:Lightbox
	 * @param {jQuery} $elements - Set of anchor elements to create the lightbox from.
	 * @param {number} [startIndex=0] - (Zero-based) index of the element to be shown first.
	 * @return {jQuery}
	 */
	var open = function ($elements, startIndex) {
		startIndex = startIndex || 0;

		// bail if there are no elements
		if (!$elements.length) {
			return;
		}

		var insertString = '';
		var initialCall = false;
		var sliderInstance;

		// if the $lightbox element doesn't exist yet, create it
		if (!$lightbox) {
			insertString += '<div class="' + options.classNames.root + ' ' + options.classNames.inactive + '">';
			insertString += '<div class="' + options.classNames.inner + '">';
			insertString += '<div class="' + options.classNames.close + '"></div>';
			insertString += '<div class="en-susi">';
			insertString += '<ul class="en-susitems">';
			insertString += '</ul>';
			insertString += '</div>';
			insertString += '</div>';
			insertString += '</div>';

			// cache elements
			$lightbox = $(insertString);
			$container = $lightbox.find('.en-susitems');

			// append the lightbox
			$(options.container).append($lightbox);

			insertString = '';

			initialCall = true;
		}

		// put the elements into the slider
		$elements.each(function () {
			var $element = $(this);
			var url = $element.attr('href');
			var caption = $element.attr(options.dataAttributes.caption);
			var description = $element.attr(options.dataAttributes.description);

			if (url) {
				insertString += '<li class="en-susitem" ' + options.dataAttributes.url + '="' + url + '">';
				insertString += '<div class="' + options.classNames.imageWrapper + '">';

				if (caption || description) {
					insertString += '<div class="' + options.classNames.text + '">';
				}

				if (caption) {
					insertString += '<div class="' + options.classNames.caption + '">' + caption + '</div>';
				}

				if (description) {
					insertString += '<div class="' + options.classNames.description + '">' + description + '</div>';
				}

				if (caption || description) {
					insertString += '</div>';
				}

				insertString += '</div>';
				insertString += '</li>';
			}
		});

		$container.html(insertString);

		// create the slider instance
		sliderInstance = new EN.Susi($container.parent(), {
			startIndex: startIndex,
			bullets: false,
			loop: false,
			callback: function (index) {
				loadImage(index);
			}
		});

		// set classes
		if (initialCall) {

			// in order to display the transition, the classes have to be removed after a timeout on first call
			setTimeout(function () {
				$lightbox.removeClass(options.classNames.inactive);
				$(document.body).addClass(options.classNames.body);
			}, 1);
		}
		else {
			$lightbox.removeClass(options.classNames.inactive);
			$(document.body).addClass(options.classNames.body);
		}

		// focus the slider for key support
		// the element needs to be visible for focus to work, thus the timeout
		setTimeout(function () {
			$container.parent().focus();
		}, 500);

		// load the first image
		loadImage(startIndex);

		return $lightbox;
	};

	/**
	 * Sets classes to the lightbox that causes it to fade out. Fade-out logic is entirely done in CSS.
	 * @memberof module:Lightbox
	 */
	var close = function () {
		if ($lightbox) {
			$lightbox.addClass(options.classNames.inactive);
			$(document.body).removeClass(options.classNames.body);
		}
	};

	/**
	 * Sets up all event listeners and sets options.
	 * @memberof module:Lightbox
	 * @param {object} [settings] - Overrides defaults, see below.
	 * @property {string} selector=".en-lightbox-trigger[href]" - Element selector for the trigger elements.
	 * @property {string} scope - Selector to group elements so that the lightbox doesn't include all elements on the page.
	 * @property {string} container="body" - Element selector to place the lightbox in.
	 * @property {object} classNames - Collection of class names that are set by the module.
	 * @property {object} dataAttributes - Collection of data attribute names that are set by the module.
	 */
	var init = function (settings) {
		if (typeof EN.Susi !== 'undefined') {
			options = $.extend({}, defaults, settings);

			var clickCaptures = [
				'.' + options.classNames.image,
				'.' + options.classNames.text,
				'.' + options.classNames.root + ' .en-susi__arrows'
			];

			// open the lightbox on click
			$(document).on('click.Lightbox', options.selector, function (e) {
				e.preventDefault();

				var $this = $(this);
				var $scope = $this.parents(options.scope);
				var $elements, index;

				if (!$scope.length) {
					$scope = $(document.body);
				}

				$elements = $scope.find(options.selector);
				index = $elements.index($this);

				open($elements, index);
			});

			// close the lightbox if an element outside the `clickCaptures` is clicked
			$(document).on('click.Lightbox', '.' + options.classNames.root, close);
			$(document).on('click.Lightbox', clickCaptures.join(','), function (e) {
				e.stopPropagation();
			});

			// close the lightbox when the ESC key is pressed
			$(document).on('keydown.Lightbox', function (e) {
				if (e.keyCode === 27) {
					close();
				}
			});
		}
		else {
			console.error('Lightbox.js requires Susi.js. Make sure to include it before initializing.');
		}
	};

	// exports
	return {
		init: init,
		open: open,
		close: close
	};
}(jQuery));
